import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  CheckCircle,
  Check,
  Info,
  Flag,
  Pencil,
  CloudCheck,
  AlertTriangle,
  ArrowUpRight,
} from 'lucide-react';
import { readOrgContext, writeOrgContext, Organisation, Background } from '../utils/orgContext';
import { useOrganisationGrounding, GroundResult } from '../hooks/useOrganisationGrounding';
import { useEngagement } from '../hooks/useEngagement';
import type { UseDoneProjectionActionReturn } from '../hooks/useDoneProjection';
import '../styles/OrganisationContext.css';

// Organisation profile workspace. AI drafts are review material until the lead
// confirms them; pain-point capture intentionally does not live on this screen.

const THINKING_MS = 650;

type BackgroundPhase = 'idle' | 'looking' | 'drafted' | 'confirmed' | 'unavailable';
type BackgroundSource = { title: string; url: string };
type OrganisationDraft = { name: string; descriptor: string; website: string };

function useDoneProjectionActionHook(args: {
  screenId: string;
  saveCurrent: () => Promise<void>;
}): UseDoneProjectionActionReturn {
  // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
  const mod = require('../hooks/useDoneProjection') as {
    useDoneProjectionAction: (args: {
      screenId: string;
      saveCurrent: () => Promise<void>;
    }) => UseDoneProjectionActionReturn;
  };
  return mod.useDoneProjectionAction(args);
}

function organisationFromDraft(draft: OrganisationDraft): Organisation | null {
  const trimmedName = draft.name.trim();
  if (!trimmedName) return null;
  return {
    name: trimmedName,
    descriptor: draft.descriptor.trim() || undefined,
    website: draft.website.trim() || undefined,
  };
}

type OrgFormProps = {
  initial: Organisation | null;
  onSet: (org: Organisation) => void;
  onDraftChange?: (draft: OrganisationDraft) => void;
  onCancel?: () => void;
};

const OrgForm: React.FC<OrgFormProps> = ({ initial, onSet, onDraftChange, onCancel }) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [descriptor, setDescriptor] = useState(initial?.descriptor ?? '');
  const [website, setWebsite] = useState(initial?.website ?? '');
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const submit = useCallback(() => {
    const org = organisationFromDraft({ name, descriptor, website });
    if (!org) return;
    onSet(org);
  }, [name, descriptor, website, onSet]);

  return (
    <div className="oc-orgform">
      <label className="oc-field-label">
        <span className="oc-field-caption">Organisation name</span>
        <input
          ref={nameRef}
          className="oc-field"
          type="text"
          aria-label="Organisation name"
          value={name}
          onChange={(e) => {
            const next = e.target.value;
            setName(next);
            onDraftChange?.({ name: next, descriptor, website });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="e.g. Acme Insurance"
        />
      </label>
      <label className="oc-field-label">
        <span className="oc-field-caption">What they do</span>
        <input
          className="oc-field"
          type="text"
          aria-label="what they do"
          value={descriptor}
          onChange={(e) => {
            const next = e.target.value;
            setDescriptor(next);
            onDraftChange?.({ name, descriptor: next, website });
          }}
          placeholder="One line, e.g. UK motor and home insurer (optional)"
        />
      </label>
      <label className="oc-field-label">
        <span className="oc-field-caption">Website / domain</span>
        <input
          className="oc-field"
          type="text"
          aria-label="website / domain"
          value={website}
          onChange={(e) => {
            const next = e.target.value;
            setWebsite(next);
            onDraftChange?.({ name, descriptor, website: next });
          }}
          placeholder="e.g. acme.co.uk (optional)"
        />
      </label>
      <div className="oc-orgform-actions">
        <button type="button" className="oc-primary" onClick={submit}>
          Set organisation
        </button>
        {onCancel ? (
          <button type="button" className="oc-link-btn" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </div>
  );
};

type OrgCardProps = { org: Organisation; onEdit: () => void };

const OrgCard: React.FC<OrgCardProps> = ({ org, onEdit }) => {
  const initial = org.name.trim().charAt(0).toUpperCase() || '?';
  const href = org.website
    ? /^https?:\/\//i.test(org.website)
      ? org.website
      : `https://${org.website}`
    : undefined;

  return (
    <div className="oc-org-card" data-testid="org-card">
      <div className="oc-org-avatar" aria-hidden="true">
        {initial}
      </div>
      <div className="oc-org-body">
        <div className="oc-org-name">{org.name}</div>
        <div className="oc-org-meta">
          {org.descriptor ? <span>{org.descriptor}</span> : null}
          {org.descriptor && org.website ? (
            <span className="oc-org-sep" aria-hidden="true">
              &middot;
            </span>
          ) : null}
          {org.website && href ? (
            <a className="oc-org-link" href={href} target="_blank" rel="noreferrer noopener">
              {org.website}
              <ArrowUpRight size={12} aria-hidden="true" />
            </a>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        className="oc-rowact"
        aria-label="Edit organisation"
        title="Edit organisation"
        onClick={onEdit}
      >
        <Pencil size={17} aria-hidden="true" />
      </button>
    </div>
  );
};

function renderBackgroundText(text: string, flaggedClaims: string[]): React.ReactNode {
  const claims = (flaggedClaims || []).filter((c) => typeof c === 'string' && c.length > 0);
  if (claims.length === 0) return text;
  const escaped = claims.map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`, 'g');
  const parts = text.split(re);
  return parts.map((part, i) =>
    claims.includes(part) ? (
      <mark
        key={`flagged-${i}`}
        className="oc-flagged"
        title="Unverified figure - researched from public sources, please verify"
      >
        {part}
      </mark>
    ) : (
      <React.Fragment key={`text-${i}`}>{part}</React.Fragment>
    ),
  );
}

const SourceList: React.FC<{ sources: BackgroundSource[] }> = ({ sources }) => {
  if (sources.length === 0) return null;
  return (
    <div className="oc-bg-sources">
      <span className="oc-bg-sources-label">Sources</span>
      {sources.map((source) => (
        <a
          key={source.url}
          className="oc-bg-source"
          href={source.url}
          target="_blank"
          rel="noreferrer noopener"
        >
          {source.title}
          <ArrowUpRight size={12} aria-hidden="true" />
        </a>
      ))}
    </div>
  );
};

const OrganisationContext: React.FC = () => {
  const initial = useMemo(() => readOrgContext(), []);
  const legacyPainPoints = useRef(initial.painPoints ?? []);

  const [organisation, setOrganisation] = useState<Organisation | null>(
    initial.organisation ?? null,
  );
  const [editingOrg, setEditingOrg] = useState(false);
  const [orgFormDraft, setOrgFormDraft] = useState<OrganisationDraft | null>(null);
  const [background, setBackground] = useState<Background | null>(initial.background ?? null);

  const [phase, setPhase] = useState<BackgroundPhase>(() => {
    if (!initial.background) return 'idle';
    if (initial.background.confirmed) return 'confirmed';
    if (initial.background.provenance === 'inferred') return 'drafted';
    return 'confirmed';
  });
  const [attribution, setAttribution] = useState<string>(
    initial.background?.provenance === 'inferred'
      ? 'Researched from public web sources - review before confirming.'
      : '',
  );
  const [draftText, setDraftText] = useState<string>(initial.background?.text ?? '');
  const [flaggedClaims, setFlaggedClaims] = useState<string[]>(
    initial.background?.flaggedClaims ?? [],
  );
  const [backgroundSources, setBackgroundSources] = useState<BackgroundSource[]>(
    initial.background?.sources ?? [],
  );

  const ground = useOrganisationGrounding()?.ground;
  const { currentEngagementId, saveOrganisation } = useEngagement();
  const thinkingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showOrgForm = !organisation || editingOrg;

  const currentOrganisationContext = useCallback(() => {
    const draftedOrganisation =
      showOrgForm && orgFormDraft ? organisationFromDraft(orgFormDraft) : null;
    return {
      organisation: draftedOrganisation || organisation,
      background,
      painPoints: legacyPainPoints.current,
    };
  }, [background, orgFormDraft, organisation, showOrgForm]);

  const saveCurrentOrganisation = useCallback(async () => {
    const ctx = currentOrganisationContext();
    if (showOrgForm && ctx.organisation) {
      setOrganisation(ctx.organisation);
      setEditingOrg(false);
      setOrgFormDraft(null);
    }
    if (currentEngagementId) {
      await saveOrganisation(ctx);
    } else {
      writeOrgContext(ctx);
    }
  }, [currentEngagementId, currentOrganisationContext, saveOrganisation, showOrgForm]);

  const {
    doneStatus,
    doneBusy,
    repoStatusText,
    markDoneDirty,
    onDone,
  } = useDoneProjectionActionHook({
    screenId: 'organisation',
    saveCurrent: saveCurrentOrganisation,
  });

  useEffect(() => {
    const ctx = { organisation, background, painPoints: legacyPainPoints.current };
    if (currentEngagementId) {
      void saveOrganisation(ctx);
    } else {
      writeOrgContext(ctx);
    }
  }, [organisation, background, currentEngagementId, saveOrganisation]);

  useEffect(
    () => () => {
      if (thinkingTimer.current) clearTimeout(thinkingTimer.current);
    },
    [],
  );

  const doneStatusInitialised = useRef(false);
  useEffect(() => {
    if (doneStatusInitialised.current) {
      markDoneDirty();
    } else {
      doneStatusInitialised.current = true;
    }
  }, [background, markDoneDirty, organisation]);

  const onSetOrg = useCallback((org: Organisation) => {
    setOrganisation(org);
    setEditingOrg(false);
    setOrgFormDraft(null);
    markDoneDirty();
  }, [markDoneDirty]);

  const onOrgDraftChange = useCallback((draft: OrganisationDraft) => {
    setOrgFormDraft(draft);
    markDoneDirty();
  }, [markDoneDirty]);

  const runGrounding = useCallback(
    async (org: Organisation) => {
      const result: GroundResult | undefined = ground ? await ground(org) : undefined;
      if (result && result.ok && result.background) {
        const text = result.background.text ?? '';
        const claims = Array.isArray(result.background.flaggedClaims)
          ? result.background.flaggedClaims
          : [];
        const sources = Array.isArray(result.background.sources) ? result.background.sources : [];
        setDraftText(text);
        setFlaggedClaims(claims);
        setBackgroundSources(sources);
        setAttribution(
          result.background.attribution ||
            'Researched from public web sources - review before confirming.',
        );
        setPhase('drafted');
        setBackground({
          text,
          provenance: 'inferred',
          confirmed: false,
          flaggedClaims: claims,
          sources,
        });
      } else {
        setFlaggedClaims([]);
        setBackgroundSources([]);
        setAttribution('');
        setPhase('unavailable');
      }
    },
    [ground],
  );

  const onDraftBackground = useCallback(() => {
    if (!organisation) return;
    setPhase('looking');
    if (thinkingTimer.current) clearTimeout(thinkingTimer.current);
    thinkingTimer.current = setTimeout(() => {
      thinkingTimer.current = null;
      void runGrounding(organisation);
    }, THINKING_MS);
  }, [organisation, runGrounding]);

  const onBackgroundTextChange = useCallback(
    (value: string) => {
      setDraftText(value);
      setBackground((prev) =>
        prev
          ? { ...prev, text: value }
          : {
              text: value,
              provenance: 'inferred',
              confirmed: false,
              sources: backgroundSources,
            },
      );
    },
    [backgroundSources],
  );

  const onConfirmBackground = useCallback(() => {
    const text = draftText;
    setBackground({
      text,
      provenance: 'stated',
      confirmed: true,
      sources: backgroundSources.length ? backgroundSources : undefined,
    });
    setPhase('confirmed');
  }, [draftText, backgroundSources]);

  return (
    <div className="oc-root" data-testid="organisation-context">
      <header className="oc-header">
        <div className="oc-header-titles">
          <div className="oc-eyebrow">Setup &middot; Step 1 of 3</div>
          <div className="oc-header-title">Company context</div>
        </div>
        <div className="oc-header-spacer" />
        <div className="oc-saved" role="status" aria-live="polite">
          <CloudCheck size={17} className="oc-saved-icon" aria-hidden="true" />
          Saved
        </div>
        {repoStatusText ? (
          <div className={`oc-repo-status oc-repo-status-${doneStatus}`} role="status" aria-live="polite">
            {repoStatusText}
          </div>
        ) : null}
        <Link
          to="/"
          className="oc-done"
          aria-disabled={doneBusy}
          onClick={(event) => {
            event.preventDefault();
            void onDone();
          }}
        >
          {doneBusy ? 'Saving...' : 'Done'}
        </Link>
      </header>

      <div className="oc-scroll">
        <div className="oc-workspace">
          <h1 className="oc-prompt">Who are we working with?</h1>
          <p className="oc-framing">
            Set the organisation, and we will research public sources to draft an executive
            background for the engagement. Review it in your own words before it becomes the
            profile context for ArcPilot.
          </p>

          <div className="oc-section-head">
            <span className="oc-step" aria-hidden="true">
              1
            </span>
            <span className="oc-section-label">The organisation</span>
          </div>
          {showOrgForm ? (
            <OrgForm
              initial={organisation}
              onSet={onSetOrg}
              onDraftChange={onOrgDraftChange}
              onCancel={organisation ? () => setEditingOrg(false) : undefined}
            />
          ) : (
            <OrgCard org={organisation as Organisation} onEdit={() => setEditingOrg(true)} />
          )}

          {organisation && !editingOrg ? (
            <>
              <div className="oc-section-head">
                <span className="oc-step" aria-hidden="true">
                  2
                </span>
                <span className="oc-section-label">Executive background</span>
              </div>

              {phase === 'idle' ? (
                <div className="oc-bg-idle">
                  <button type="button" className="oc-primary" onClick={onDraftBackground}>
                    <Sparkles size={16} aria-hidden="true" /> Draft the background
                  </button>
                  <p className="oc-bg-idle-note">
                    We will look across public web sources and produce a deeper executive profile
                    for {organisation.name}.
                  </p>
                </div>
              ) : null}

              {phase === 'looking' ? (
                <div
                  className="oc-thinking"
                  role="status"
                  aria-live="polite"
                  aria-label={`Looking up what's known about ${organisation.name}`}
                >
                  <span className="oc-dots" aria-hidden="true">
                    <span className="oc-dot" />
                    <span className="oc-dot" />
                    <span className="oc-dot" />
                  </span>
                  Looking up what is known about {organisation.name}...
                </div>
              ) : null}

              {phase === 'drafted' ? (
                <div className="oc-bg-draft" data-testid="background-draft">
                  <div className="oc-bg-draft-head">
                    <span className="oc-tag oc-tag-draft">
                      <Sparkles size={12} aria-hidden="true" /> AI draft - review
                    </span>
                    <span className="oc-bg-attribution">
                      <Info size={13} aria-hidden="true" />{' '}
                      {attribution || 'Researched from public web sources - review before confirming.'}
                    </span>
                  </div>
                  <div className="oc-bg-readout" aria-hidden="true">
                    {renderBackgroundText(draftText, flaggedClaims)}
                  </div>
                  <textarea
                    className="oc-field oc-bg-textarea"
                    aria-label="Background"
                    value={draftText}
                    onChange={(e) => onBackgroundTextChange(e.target.value)}
                    rows={8}
                  />
                  <SourceList sources={backgroundSources} />
                  <div className="oc-bg-actions">
                    <button type="button" className="oc-primary-sm" onClick={onConfirmBackground}>
                      <CheckCircle size={15} aria-hidden="true" /> Confirm background
                    </button>
                    <button type="button" className="oc-link-btn" onClick={onDraftBackground}>
                      <Sparkles size={14} aria-hidden="true" /> Regenerate background
                    </button>
                    <span className="oc-bg-flag-legend">
                      <Flag size={13} aria-hidden="true" /> highlighted = review-needed claim
                    </span>
                  </div>
                </div>
              ) : null}

              {phase === 'unavailable' ? (
                <div className="oc-bg-manual">
                  <div className="oc-alert" role="alert">
                    <AlertTriangle size={15} className="oc-alert-icon" aria-hidden="true" />
                    <span>
                      We could not look up {organisation.name} just now. You can still write the
                      executive background yourself below and save it.
                    </span>
                  </div>
                  <textarea
                    className="oc-field oc-bg-textarea"
                    aria-label="Background"
                    value={draftText}
                    onChange={(e) => onBackgroundTextChange(e.target.value)}
                    placeholder="Write an executive background for the organisation, in your own words."
                    rows={8}
                  />
                  <div className="oc-bg-actions">
                    <button type="button" className="oc-primary-sm" onClick={onConfirmBackground}>
                      <CheckCircle size={15} aria-hidden="true" /> Save background
                    </button>
                  </div>
                </div>
              ) : null}

              {phase === 'confirmed' ? (
                <div className="oc-bg-confirmed">
                  <div className="oc-bg-confirmed-head">
                    <span className="oc-tag oc-tag-confirmed">
                      <CheckCircle size={12} aria-hidden="true" /> Confirmed
                    </span>
                    <button
                      type="button"
                      className="oc-link-btn"
                      onClick={() =>
                        setPhase(attribution || backgroundSources.length ? 'drafted' : 'unavailable')
                      }
                    >
                      <Pencil size={14} aria-hidden="true" /> Edit
                    </button>
                    <button type="button" className="oc-link-btn" onClick={onDraftBackground}>
                      <Sparkles size={14} aria-hidden="true" /> Regenerate background
                    </button>
                  </div>
                  <div className="oc-bg-confirmed-text">{draftText}</div>
                  <SourceList sources={backgroundSources} />
                </div>
              ) : null}

              <div className="oc-legend">
                <span className="oc-legend-title">How this works:</span>
                <span className="oc-legend-item">
                  <span className="oc-tag oc-tag-draft">
                    <Sparkles size={11} aria-hidden="true" /> AI draft
                  </span>{' '}
                  researched online, never fact until you confirm
                </span>
                <span className="oc-legend-item">
                  <span className="oc-tag oc-tag-confirmed">
                    <Check size={11} aria-hidden="true" /> Confirmed - your words
                  </span>{' '}
                  signed off
                </span>
                <span className="oc-legend-item">
                  <Flag size={13} aria-hidden="true" /> review-needed claim
                </span>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default OrganisationContext;
